// Memberships & passes, the MEMBER side (MEMBERSHIPS_PLAN.md §7): the club's
// types as a joiner sees them, eligibility, applying, what I hold and pay for,
// cancelling, health answers, the parent-or-carer page and the pay-for-someone
// page. Every call is one function here; the money moves through
// useMembershipPayments (A2's contracts). Words live in utils/membershipsMember.
import {ref} from 'vue';
import {useHttpRequest} from '~/composables/useHttpRequest';
import {endpoints} from '~/network/endpoints';
import {TYPE_STATUS_INT, APPLICATION_STATUS_INT} from '~/utils/memberships';

export {membershipApiError} from '~/composables/useMembershipsAdmin';

export function useMembershipsMember() {
  const {httpRequest} = useHttpRequest();
  const pending = ref(false);

  const call = async (endpoint, body) => {
    pending.value = true;
    try {
      return await httpRequest(endpoint, body ?? {});
    } finally {
      pending.value = false;
    }
  };

  // ── The club's types, as a joiner ──
  /** Live types the viewer may see (the API scopes: Live + Public for non-admins; hidden ones open by link). */
  const listClubTypes = async (clubId, limit = 50) => {
    const res = await httpRequest(endpoints.memberships.types.getItems,
        {page: 1, limit, filters: {clubIds: [clubId], statuses: [TYPE_STATUS_INT.Live], forJoining: true}});
    return res?.items ?? [];
  };
  const getType = (typeId) => httpRequest(endpoints.memberships.types.getItem(typeId));
  const eligibility = (typeId, {priceOptionId = null, periodId = null, playerId = null} = {}) =>
      httpRequest(endpoints.memberships.types.eligibility(typeId, {priceOptionId, periodId, playerId}), null);

  // ── Applying (D34) ──
  const apply = (body) => call(endpoints.memberships.applications.apply, body);
  const withdrawApplication = (id) => call(endpoints.memberships.applications.withdraw(id));
  const changeParent = (id, {name, email, relationship}) =>
      call(endpoints.memberships.applications.resendParentConsent(id), {name, email, relationship});
  const askSomeoneElseToPay = (id, {name, email}) =>
      call(endpoints.memberships.applications.askSomeoneElseToPay(id), {name: name || null, email});
  const getApplication = (id) => httpRequest(endpoints.memberships.applications.getItem(id));
  /** My open applications (the API scopes applicants to their own rows). */
  const myApplications = async () => {
    const res = await httpRequest(endpoints.memberships.applications.getItems, {
      page: 1, limit: 50,
      filters: {statuses: [APPLICATION_STATUS_INT.Pending, APPLICATION_STATUS_INT.AwaitingParent, APPLICATION_STATUS_INT.AwaitingLink]},
    });
    return res?.items ?? [];
  };

  // ── Mine and the ones I pay for ──
  const mine = () => httpRequest(endpoints.memberships.memberships.mine, null);
  const getMembership = (id) => httpRequest(endpoints.memberships.memberships.getItem(id));
  const cancel = (id, {now = false, reason = null} = {}) =>
      call(endpoints.memberships.memberships.cancel(id), {now: !!now, reason: reason || null});
  const cancelPreview = (id) => httpRequest(endpoints.memberships.memberships.cancelPreview(id), null);
  const resume = (id) => call(endpoints.memberships.memberships.resume(id));
  const setAutoRenew = (id, autoRenew) => call(endpoints.memberships.memberships.updateItem(id), {id, autoRenew});

  // ── Health answers (D24: mine, any time) ──
  const myHealth = (responseId) => httpRequest(endpoints.memberships.memberships.health(responseId, 'web-member'), null);
  const updateHealth = (responseId, {answers, confirmOnly = false}) =>
      call(endpoints.memberships.memberships.updateHealth(responseId), {answers: answers ?? {}, confirmOnly: !!confirmOnly});
  const withdrawHealth = (responseId) => call(endpoints.memberships.memberships.withdrawHealth(responseId));
  // D42: someone named on a group answers their own club and health questions (returns the membership detail).
  const submitOwnAnswers = (id, {answers, health = null}) => call(endpoints.memberships.memberships.answers(id), {answers: answers ?? [], health});

  // ── D45: the child's family confirms a parent's link (no login) ──
  const linkPage = (token) => httpRequest(endpoints.memberships.linkConfirmations.get(token), null);
  const answerLink = (token, agree) => call(endpoints.memberships.linkConfirmations.answer(token), {agree: !!agree});

  // ── Parent or carer page (no login) ──
  const consentPage = (token) => httpRequest(endpoints.memberships.parentConsents.get(token), null);
  const sendHealthCode = (token) => call(endpoints.memberships.parentConsents.sendHealthCode(token));
  const verifyHealthCode = (token, code) => call(endpoints.memberships.parentConsents.verifyHealthCode(token), {code});
  const answerConsent = (token, {agree, acceptTerms = false, healthAnswers = null, healthConsent = null}) =>
      call(endpoints.memberships.parentConsents.answer(token), {agree: !!agree, acceptTerms: !!acceptTerms, healthAnswers, healthConsent});

  return {
    pending,
    listClubTypes, getType, eligibility,
    apply, withdrawApplication, changeParent, askSomeoneElseToPay, getApplication, myApplications,
    mine, getMembership, cancel, cancelPreview, resume, setAutoRenew,
    myHealth, updateHealth, withdrawHealth, submitOwnAnswers, linkPage, answerLink,
    consentPage, sendHealthCode, verifyHealthCode, answerConsent,
  };
}
